import { useCallback, useMemo } from "react";

import { useControlledField } from "./useControlledField";

/** Minimal option shape for label resolution — structurally satisfied by
 *  `ListboxOption` (Select/Combobox options) without coupling this util to it. */
type LabelledOption = { value: string; label: string };

export type UseMultiValueFieldParams = {
  /** Controlled set; omit for the uncontrolled (internal-state) path. */
  values?: string[];
  onValuesChange?: (values: string[]) => void;
  /** Options to resolve each selected value to a display label for `selectedItems`. */
  options?: readonly LabelledOption[];
};

export type UseMultiValueFieldResult = {
  values: string[];
  setValues: (next: string[]) => void;
  removeValue: (value: string) => void;
  /** Selected values paired with their display label (falls back to the raw
   *  value when no option matches), ready for `ChipGroup`. */
  selectedItems: LabelledOption[];
};

/**
 * Controlled/uncontrolled plumbing for a multi-select value set, shared by the
 * multi `Select` and `Combobox` fields so add/remove and chip-label resolution
 * aren't reimplemented (and drifting) per field. Wraps `useControlledField<string[]>`;
 * uncontrolled usage (no `values`) keeps the set in internal state.
 */
export function useMultiValueField({
  values,
  onValuesChange,
  options,
}: UseMultiValueFieldParams): UseMultiValueFieldResult {
  const field = useControlledField<string[]>({
    value: values,
    defaultValue: [],
    onChange: onValuesChange,
  });
  const { setValue, value: fieldValue } = field;
  // Stable identity while the set is unchanged, so `selectedItems` (and the chips
  // it feeds) don't churn on unrelated re-renders like Combobox keystrokes.
  const current = useMemo(() => fieldValue ?? [], [fieldValue]);

  const removeValue = useCallback(
    (value: string) =>
      setValue(current.filter(candidate => candidate !== value)),
    [setValue, current],
  );

  const labelByValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of options ?? []) {
      map.set(option.value, option.label);
    }
    return map;
  }, [options]);

  const selectedItems = useMemo(
    () =>
      current.map(value => ({
        value,
        label: labelByValue.get(value) ?? value,
      })),
    [current, labelByValue],
  );

  return { values: current, setValues: setValue, removeValue, selectedItems };
}
